import { useMutation, UseMutationOptions, useQuery } from "react-query";
import { isEmpty } from "lodash";
import { fetchApi, retryUnlessClientError } from "../../helpers/reactQueryApi";
import type {
  AppCreatorApp,
  AppCreatorItem,
  AppLinkResponse,
  LoadAppBySlugArgs,
  LoadItemsArgs,
  LoadAppArgs,
  LoadItemArgs,
  UpdateItemArgs,
  CreateItemArgs,
  LoadItemsResponse,
} from "./types";
import { AppWithLink } from "../administration/appCreator/api";

export const appsApiBaseUrl = () => "/api/apps";
export const appUrl = ({ externalAppId, appId }) => {
  if (isEmpty(externalAppId) && isEmpty(appId))
    throw new Error("appId or externalAppId required to build appUrl");

  return [
    appsApiBaseUrl(),
    "apps",
    externalAppId ? `external/${externalAppId}` : appId,
  ].join("/");
};

export const itemUrl = ({ appId, externalAppId, itemId, externalItemId }) => {
  if (isEmpty(itemId) && isEmpty(externalItemId))
    throw new Error("itemId or externalItemId required to build itemUrl");

  return [
    appUrl({ appId, externalAppId }),
    "items",
    externalItemId ? `external/${externalItemId}` : itemId,
  ].join("/");
};

export const useLoadApp = ({ appId, externalAppId }: LoadAppArgs, options?) => {
  const isEnabled = !!appId || !!externalAppId;

  return useQuery<AppCreatorApp>(
    isEnabled ? appUrl({ appId, externalAppId }) : "",
    {
      enabled: isEnabled,
      retry: retryUnlessClientError,
      ...options,
    },
  );
};

export const useLoadAppBySlug = (
  { slug, groupSlug }: LoadAppBySlugArgs,
  options?,
) => {
  // First, resolve the slug to get the app ID
  const appLinkUrl = groupSlug
    ? `/groups/${groupSlug}/apps/${slug}`
    : `/apps/${slug}`;

  const appLinkQuery = useQuery<AppLinkResponse>(appLinkUrl, {
    enabled: !!slug,
    retry: retryUnlessClientError,
    ...options,
  });

  // Then load the full app data using the resolved app ID
  const appQuery = useLoadApp(
    { appId: appLinkQuery.data?.app_id },
    {
      enabled: !!appLinkQuery.data?.app_id,
      ...options,
    },
  );

  return {
    ...appQuery,
    data:
      appQuery.data && appLinkQuery.data
        ? ({ ...appQuery.data, _link: appLinkQuery.data } as AppWithLink)
        : null,
    isLoading: appLinkQuery.isLoading || appQuery.isLoading,
    error: appLinkQuery.error || appQuery.error,
    appLink: appLinkQuery.data,
  };
};

export const useLoadItem = (
  { appId, externalAppId, itemId, externalItemId }: LoadItemArgs,
  options = {},
) => {
  const isEnabled =
    (!!externalItemId || !!itemId) && (!!appId || !!externalAppId);

  return useQuery<AppCreatorItem>(
    isEnabled ? itemUrl({ appId, externalAppId, itemId, externalItemId }) : "",
    {
      enabled: isEnabled,
      retry: retryUnlessClientError,
      ...options,
    },
  );
};

export function useUpdateItem(
  options?: UseMutationOptions<unknown, Error, UpdateItemArgs>,
) {
  return useMutation(
    ({ appId, externalAppId, itemId, externalItemId, body }: UpdateItemArgs) =>
      fetchApi(itemUrl({ appId, externalAppId, itemId, externalItemId }), {
        method: "PUT",
        body,
      }),
    options,
  );
}

export function useCreateItem(
  options?: UseMutationOptions<{ id: string }, Error, CreateItemArgs>,
) {
  return useMutation(
    ({ appId, externalAppId, body }: CreateItemArgs) =>
      fetchApi(`${appUrl({ appId, externalAppId })}/items`, {
        method: "POST",
        body,
      }),
    options,
  );
}

// App subscription mutations
export function useSubscribeToApp(
  options?: UseMutationOptions<
    unknown,
    Error,
    { appId?: string; externalAppId?: string }
  >,
) {
  return useMutation(
    ({ appId, externalAppId }) =>
      fetchApi(`${appUrl({ appId, externalAppId })}/subscription`, {
        method: "PUT",
      }),
    options,
  );
}

export function useUnsubscribeFromApp(
  options?: UseMutationOptions<
    unknown,
    Error,
    { appId?: string; externalAppId?: string }
  >,
) {
  return useMutation(
    ({ appId, externalAppId }) =>
      fetchApi(`${appUrl({ appId, externalAppId })}/subscription`, {
        method: "DELETE",
      }),
    options,
  );
}

export interface DeleteItemArgs {
  appId?: string;
  externalAppId?: string;
  itemId?: string;
  externalItemId?: string;
}

export function useDeleteItem(
  options?: UseMutationOptions<unknown, Error, DeleteItemArgs>,
) {
  return useMutation(
    ({ appId, externalAppId, itemId, externalItemId }: DeleteItemArgs) =>
      fetchApi(itemUrl({ appId, externalAppId, itemId, externalItemId }), {
        method: "DELETE",
      }),
    options,
  );
}

export const useLoadItems = (
  {
    appId,
    externalAppId,
    view = "table",
    archived = false,
    ...params
  }: LoadItemsArgs,
  options?,
) => {
  const searchParams = new URLSearchParams();

  if (view) searchParams.set("view", view);
  if (archived) searchParams.set("archived", "true");

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.set(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  const isEnabled = !!appId || !!externalAppId;
  const itemsUrl = isEnabled
    ? `${appUrl({ appId, externalAppId })}/items${queryString ? `?${queryString}` : ""}`
    : "";

  return useQuery<LoadItemsResponse>(
    itemsUrl,
    () =>
      fetchApi(itemsUrl, { parsePaginationHeaders: true }).then((response) => ({
        items: response.payload,
        pagination: response.pagination,
      })),
    { enabled: isEnabled, retry: retryUnlessClientError, ...options },
  );
};

// Export functionality
export interface ItemsExportStatus {
  status: "initialized" | "in_progress" | "finished" | "error";
  progress: number;
  files?: Array<{
    id: string;
    extension: string;
  }>;
  created_at?: string;
}

export const itemsExportUrl = ({ appId, externalAppId }) =>
  `${appUrl({ appId, externalAppId })}/items/exports`;

export const itemsExportStatusUrl = ({ slug, groupSlug }) => {
  return groupSlug
    ? `/groups/${groupSlug}/apps/${slug}/items/exports/last`
    : `/apps/${slug}/items/exports/last`;
};

export const itemsExportCreateUrl = ({ slug, groupSlug }) => {
  return groupSlug
    ? `/groups/${groupSlug}/apps/${slug}/items/exports`
    : `/apps/${slug}/items/exports`;
};

export const useLoadItemsExportStatus = (
  { slug, groupSlug }: { slug?: string; groupSlug?: string },
  options?,
) => {
  const isEnabled = !!slug;

  return useQuery<ItemsExportStatus>(
    isEnabled ? itemsExportStatusUrl({ slug, groupSlug }) : "",
    {
      enabled: isEnabled,
      retry: retryUnlessClientError,
      refetchInterval: (data) => {
        // Auto-refetch every 3 seconds if export is in progress
        if (data?.status === "initialized" || data?.status === "in_progress") {
          return 3000;
        }
        return false;
      },
      ...options,
    },
  );
};

export function useCreateItemsExport(
  options?: UseMutationOptions<
    unknown,
    Error,
    { slug?: string; groupSlug?: string }
  >,
) {
  return useMutation(
    ({ slug, groupSlug }) =>
      fetchApi(itemsExportCreateUrl({ slug, groupSlug }), {
        method: "POST",
      }),
    options,
  );
}
